import { API_ROUTES, ApiResponse, interpolateRoute } from '@super/shared-types'
import { BaseApiClient } from './base-client'
import { assigneePreviewQuery } from './directory.client'
import type {
  AssigneePreviewParams,
  DeleteImpact,
  DirectoryAssigneePreview,
  PropertyGroup,
  PropertyGroupDeleteImpact,
  PropertyGroupWithMembers,
  ReorderRoleMembersResult,
  Role,
  RoleRotation,
  RoleRotationUpdate,
  RoleWithMembers,
  ScheduleEntry,
  ScheduleEntryWriteInput,
  Staff,
  StaffIntegrationAssignee,
} from './directory.client'

const ROUTES = API_ROUTES.ADMIN.DIRECTORY

export class DirectoryAdminApiClient extends BaseApiClient {
  // ─── Staff ────────────────────────────────────────────────────────────
  async listStaff(companyId: string): Promise<Staff[]> {
    const response = await this.request<ApiResponse<Staff[]>>(
      `${ROUTES.STAFF}?companyId=${encodeURIComponent(companyId)}`,
    )
    return response.data ?? []
  }

  async createStaff(
    companyId: string,
    input: {
      name: string
      phone?: string
      email?: string
      code?: string
      integrationAssignees?: StaffIntegrationAssignee[]
    },
  ): Promise<Staff> {
    const response = await this.request<ApiResponse<Staff>>(
      `${ROUTES.STAFF}?companyId=${encodeURIComponent(companyId)}`,
      { method: 'POST', body: JSON.stringify(input) },
    )
    return response.data!
  }

  async updateStaff(
    companyId: string,
    id: string,
    input: Partial<Omit<Staff, 'id' | 'companyId'>>,
  ): Promise<Staff> {
    const response = await this.request<ApiResponse<Staff>>(
      `${interpolateRoute(ROUTES.STAFF_BY_ID, { id })}?companyId=${encodeURIComponent(companyId)}`,
      { method: 'PATCH', body: JSON.stringify(input) },
    )
    return response.data!
  }

  async previewStaffDelete(
    companyId: string,
    id: string,
  ): Promise<DeleteImpact> {
    const response = await this.request<ApiResponse<DeleteImpact>>(
      `${interpolateRoute(ROUTES.STAFF_DELETE_PREVIEW, { id })}?companyId=${encodeURIComponent(companyId)}`,
    )
    return response.data!
  }

  async deleteStaff(
    companyId: string,
    id: string,
  ): Promise<{ ok: boolean } | null> {
    const response = await this.request<ApiResponse<{ ok: boolean }>>(
      `${interpolateRoute(ROUTES.STAFF_BY_ID, { id })}?companyId=${encodeURIComponent(companyId)}`,
      { method: 'DELETE' },
    )
    return response.data ?? null
  }

  // ─── Roles ────────────────────────────────────────────────────────────
  async listRoles(companyId: string): Promise<RoleWithMembers[]> {
    const response = await this.request<ApiResponse<RoleWithMembers[]>>(
      `${ROUTES.ROLES}?companyId=${encodeURIComponent(companyId)}`,
    )
    return response.data ?? []
  }

  async createRole(
    companyId: string,
    input: { name: string; description?: string },
  ): Promise<Role> {
    const response = await this.request<ApiResponse<Role>>(
      `${ROUTES.ROLES}?companyId=${encodeURIComponent(companyId)}`,
      { method: 'POST', body: JSON.stringify(input) },
    )
    return response.data!
  }

  async updateRole(
    companyId: string,
    id: string,
    input: Partial<Pick<Role, 'name' | 'description' | 'schedulingMode'>>,
  ): Promise<Role> {
    const response = await this.request<ApiResponse<Role>>(
      `${interpolateRoute(ROUTES.ROLES_BY_ID, { id })}?companyId=${encodeURIComponent(companyId)}`,
      { method: 'PATCH', body: JSON.stringify(input) },
    )
    return response.data!
  }

  async getRoleRotation(
    companyId: string,
    roleId: string,
  ): Promise<RoleRotation> {
    const response = await this.request<ApiResponse<RoleRotation>>(
      `${interpolateRoute(ROUTES.ROLE_ROTATION, { id: roleId })}?companyId=${encodeURIComponent(companyId)}`,
    )
    return response.data!
  }

  async updateRoleRotation(
    companyId: string,
    roleId: string,
    input: RoleRotationUpdate,
  ): Promise<RoleRotation> {
    const response = await this.request<ApiResponse<RoleRotation>>(
      `${interpolateRoute(ROUTES.ROLE_ROTATION, { id: roleId })}?companyId=${encodeURIComponent(companyId)}`,
      { method: 'PUT', body: JSON.stringify(input) },
    )
    return response.data!
  }

  async previewRoleDelete(
    companyId: string,
    id: string,
  ): Promise<DeleteImpact> {
    const response = await this.request<ApiResponse<DeleteImpact>>(
      `${interpolateRoute(ROUTES.ROLES_DELETE_PREVIEW, { id })}?companyId=${encodeURIComponent(companyId)}`,
    )
    return response.data!
  }

  async deleteRole(
    companyId: string,
    id: string,
  ): Promise<{ ok: boolean } | null> {
    const response = await this.request<ApiResponse<{ ok: boolean }>>(
      `${interpolateRoute(ROUTES.ROLES_BY_ID, { id })}?companyId=${encodeURIComponent(companyId)}`,
      { method: 'DELETE' },
    )
    return response.data ?? null
  }

  async addRoleMember(
    companyId: string,
    roleId: string,
    staffId: string,
  ): Promise<unknown> {
    const response = await this.request<ApiResponse<unknown>>(
      `${interpolateRoute(ROUTES.ROLE_MEMBERS, { id: roleId })}?companyId=${encodeURIComponent(companyId)}`,
      { method: 'POST', body: JSON.stringify({ staffId }) },
    )
    return response.data
  }

  async removeRoleMember(
    companyId: string,
    roleId: string,
    staffId: string,
  ): Promise<unknown> {
    const response = await this.request<ApiResponse<unknown>>(
      `${interpolateRoute(ROUTES.ROLE_MEMBER_BY_ID, { id: roleId, staffId })}?companyId=${encodeURIComponent(companyId)}`,
      { method: 'DELETE' },
    )
    return response.data
  }

  async reorderRoleMembers(
    companyId: string,
    roleId: string,
    staffIds: string[],
  ): Promise<ReorderRoleMembersResult | null> {
    const response = await this.request<ApiResponse<ReorderRoleMembersResult>>(
      `${interpolateRoute(ROUTES.ROLE_MEMBERS_ORDER, { id: roleId })}?companyId=${encodeURIComponent(companyId)}`,
      { method: 'PATCH', body: JSON.stringify({ staffIds }) },
    )
    return response.data ?? null
  }

  async resolveRole(
    companyId: string,
    roleId: string,
    date?: string,
  ): Promise<Staff[]> {
    const base = `${interpolateRoute(ROUTES.ROLE_RESOLVE, { id: roleId })}?companyId=${encodeURIComponent(companyId)}`
    const url = date ? `${base}&date=${encodeURIComponent(date)}` : base
    const response = await this.request<ApiResponse<Staff[]>>(url)
    return response.data ?? []
  }

  // ─── Property Groups ──────────────────────────────────────────────────
  async listPropertyGroups(
    companyId: string,
  ): Promise<PropertyGroupWithMembers[]> {
    const response = await this.request<
      ApiResponse<PropertyGroupWithMembers[]>
    >(`${ROUTES.PROPERTY_GROUPS}?companyId=${encodeURIComponent(companyId)}`)
    return response.data ?? []
  }

  async createPropertyGroup(
    companyId: string,
    input: { name: string; description?: string },
  ): Promise<PropertyGroup> {
    const response = await this.request<ApiResponse<PropertyGroup>>(
      `${ROUTES.PROPERTY_GROUPS}?companyId=${encodeURIComponent(companyId)}`,
      { method: 'POST', body: JSON.stringify(input) },
    )
    return response.data!
  }

  async updatePropertyGroup(
    companyId: string,
    id: string,
    input: { name?: string; description?: string | null },
  ): Promise<PropertyGroup> {
    const response = await this.request<ApiResponse<PropertyGroup>>(
      `${interpolateRoute(ROUTES.PROPERTY_GROUPS_BY_ID, { id })}?companyId=${encodeURIComponent(companyId)}`,
      { method: 'PATCH', body: JSON.stringify(input) },
    )
    return response.data!
  }

  async previewPropertyGroupDelete(
    companyId: string,
    id: string,
  ): Promise<PropertyGroupDeleteImpact> {
    const response = await this.request<ApiResponse<PropertyGroupDeleteImpact>>(
      `${interpolateRoute(ROUTES.PROPERTY_GROUPS_DELETE_PREVIEW, { id })}?companyId=${encodeURIComponent(companyId)}`,
    )
    return response.data!
  }

  async deletePropertyGroup(
    companyId: string,
    id: string,
  ): Promise<{ ok: boolean } | null> {
    const response = await this.request<ApiResponse<{ ok: boolean }>>(
      `${interpolateRoute(ROUTES.PROPERTY_GROUPS_BY_ID, { id })}?companyId=${encodeURIComponent(companyId)}`,
      { method: 'DELETE' },
    )
    return response.data ?? null
  }

  async setPropertyGroupMembers(
    companyId: string,
    id: string,
    propertyIds: string[],
  ): Promise<PropertyGroupWithMembers> {
    const response = await this.request<ApiResponse<PropertyGroupWithMembers>>(
      `${interpolateRoute(ROUTES.PROPERTY_GROUP_MEMBERS, { id })}?companyId=${encodeURIComponent(companyId)}`,
      { method: 'PUT', body: JSON.stringify({ propertyIds }) },
    )
    return response.data!
  }

  async removePropertyGroupMember(
    companyId: string,
    id: string,
    propertyId: string,
  ): Promise<void> {
    await this.request<ApiResponse<{ ok: boolean }>>(
      `${interpolateRoute(ROUTES.PROPERTY_GROUP_MEMBER_BY_ID, { id, propertyId })}?companyId=${encodeURIComponent(companyId)}`,
      { method: 'DELETE' },
    )
  }

  // ─── Schedule ─────────────────────────────────────────────────────────
  async listSchedule(companyId: string): Promise<ScheduleEntry[]> {
    const response = await this.request<ApiResponse<ScheduleEntry[]>>(
      `${ROUTES.SCHEDULE}?companyId=${encodeURIComponent(companyId)}`,
    )
    return response.data ?? []
  }

  async createScheduleEntry(
    companyId: string,
    input: ScheduleEntryWriteInput,
  ): Promise<ScheduleEntry> {
    const response = await this.request<ApiResponse<ScheduleEntry>>(
      `${ROUTES.SCHEDULE}?companyId=${encodeURIComponent(companyId)}`,
      { method: 'POST', body: JSON.stringify(input) },
    )
    return response.data!
  }

  async updateScheduleEntry(
    companyId: string,
    id: string,
    input: ScheduleEntryWriteInput,
  ): Promise<ScheduleEntry> {
    const response = await this.request<ApiResponse<ScheduleEntry>>(
      `${interpolateRoute(ROUTES.SCHEDULE_BY_ID, { id })}?companyId=${encodeURIComponent(companyId)}`,
      { method: 'PATCH', body: JSON.stringify(input) },
    )
    return response.data!
  }

  async deleteScheduleEntry(companyId: string, id: string): Promise<unknown> {
    const response = await this.request<ApiResponse<unknown>>(
      `${interpolateRoute(ROUTES.SCHEDULE_BY_ID, { id })}?companyId=${encodeURIComponent(companyId)}`,
      { method: 'DELETE' },
    )
    return response.data
  }

  async previewAssignee(
    companyId: string,
    actionGroupId: string,
    params: AssigneePreviewParams,
  ): Promise<DirectoryAssigneePreview> {
    // Same query builder the company-scoped client uses, plus the companyId
    // this admin twin carries — the ref params can't drift between the two.
    const query = `companyId=${encodeURIComponent(companyId)}&${assigneePreviewQuery(params)}`
    const response = await this.request<ApiResponse<DirectoryAssigneePreview>>(
      `${interpolateRoute(ROUTES.ACTION_GROUP_ASSIGNEE_PREVIEW, {
        actionGroupId,
      })}?${query}`,
    )
    return response.data!
  }
}

export const directoryAdminApiClient = new DirectoryAdminApiClient()
